import { useEffect } from 'react';

import { identifyObservabilityUser } from '@modernloop/shared/observability';
import type { ObservabilityUser } from '@modernloop/shared/observability';

import type { UserInfo } from 'src/contexts/auth0/Auth0Context';

/**
 * Builds a vendor-neutral observability user from hiring-dashboard auth info.
 * Per-vendor guards (Datadog requires org, LogRocket requires employee id) live
 * in the sinks, so this simply maps the available fields.
 */
function buildObservabilityUser(userInfo: UserInfo): ObservabilityUser {
  return {
    kind: 'EMPLOYEE',
    userId: userInfo.userId,
    employeeId: userInfo.employeeId,
    userName: userInfo.userName,
    employeeName: userInfo.employeeName,
    userEmail: userInfo.userEmail,
    employeeEmail: userInfo.employeeEmail,
    orgId: userInfo.orgId,
    orgName: userInfo.orgName,
  };
}

/**
 * Identifies the user across all registered observability vendors (Datadog,
 * LogRocket, ...) via the shared facade.
 *
 * @param userInfo from useAuth();
 */
export const useIdentifyObservabilityUser = (userInfo: UserInfo | null) => {
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes analytics APIs after render.
  useEffect(() => {
    if (!userInfo) {
      return;
    }
    identifyObservabilityUser(buildObservabilityUser(userInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userInfo)]);
};
